/**
 * Hook: useSpeechToText
 * SpecReferenceID: SPEECH-V1
 * Requirements: SPEECH-V1-R03
 *
 * React hook for transcribing audio to text using the Speech-to-Text service.
 *
 * SPEECH-V1-R03: System shall transcribe audio to normalized text using
 *                configured STT provider.
 */

import { useState, useCallback, useRef } from 'react';
import {
  speechToTextService,
  SpeechToTextService,
  type TranscriptionResult,
  type SpeechToTextConfig,
  SpeechToTextError,
} from '@/services/speech-to-text';
import type { AudioData } from '@/hooks/use-audio-capture';

// Re-export TranscriptionResult for consumer convenience
export type { TranscriptionResult } from '@/services/speech-to-text';

/**
 * Transcription state.
 */
export interface SpeechToTextState {
  /** Whether transcription is in progress */
  isTranscribing: boolean;
  /** Transcription result (null until transcription completes) */
  result: TranscriptionResult | null;
  /** Error message if transcription failed */
  error: string | null;
  /** Whether the result has low confidence */
  isLowConfidence: boolean;
}

/**
 * Hook options.
 */
export interface UseSpeechToTextOptions {
  /** Custom STT service instance (uses default if not provided) */
  service?: SpeechToTextService;
  /** Callback when transcription completes successfully */
  onTranscribed?: (result: TranscriptionResult) => void;
  /** Callback when transcription has low confidence */
  onLowConfidence?: (result: TranscriptionResult) => void;
  /** Callback when an error occurs */
  onError?: (error: string) => void;
  /** Partial configuration overrides */
  config?: Partial<SpeechToTextConfig>;
  /**
   * SPEECH-V1-R14: BCP-47 language the user has declared they will speak.
   * Forwarded to the transcription request so this path honours the same
   * choice the streaming path does; without it the service falls back to
   * its configured default regardless of what the user selected.
   */
  language?: string;
}

/**
 * Hook return type.
 */
export interface UseSpeechToTextReturn extends SpeechToTextState {
  /** Transcribe audio data to text */
  transcribe: (audioData: AudioData) => Promise<TranscriptionResult | null>;
  /** Clear the current result and error */
  clear: () => void;
  /** Update service configuration */
  updateConfig: (config: Partial<SpeechToTextConfig>) => void;
}

/**
 * React hook for speech-to-text transcription.
 *
 * SPEECH-V1-R03: Transcribe audio to normalized text.
 *
 * @param options - Hook configuration options
 * @returns Transcription state and control methods
 *
 * @example
 * ```tsx
 * const { transcribe, isTranscribing, result, error } = useSpeechToText({
 *   onTranscribed: (result) => console.log('Transcribed:', result.text),
 * });
 *
 * // After capturing audio
 * const transcription = await transcribe(audioData);
 * ```
 */
export function useSpeechToText(
  options: UseSpeechToTextOptions = {}
): UseSpeechToTextReturn {
  const { service, onTranscribed, onLowConfidence, onError, config, language } =
    options;

  // Get or create service instance
  const serviceRef = useRef<SpeechToTextService>(
    service ?? speechToTextService
  );

  // Apply configuration if provided
  if (config) {
    serviceRef.current.updateConfig(config);
  }

  // State
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [result, setResult] = useState<TranscriptionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLowConfidence, setIsLowConfidence] = useState(false);

  /**
   * Transcribe audio data to text.
   */
  const transcribe = useCallback(
    async (audioData: AudioData): Promise<TranscriptionResult | null> => {
      setIsTranscribing(true);
      setError(null);
      setResult(null);
      setIsLowConfidence(false);

      try {
        const transcriptionResult = await serviceRef.current.transcribe(
          audioData,
          language
        );

        setResult(transcriptionResult);

        // Check for low confidence
        const lowConfidence = serviceRef.current.isLowConfidence(transcriptionResult);
        setIsLowConfidence(lowConfidence);

        if (lowConfidence) {
          onLowConfidence?.(transcriptionResult);
        }

        onTranscribed?.(transcriptionResult);
        return transcriptionResult;
      } catch (err) {
        let errorMessage = 'Transcription failed';

        if (err instanceof SpeechToTextError) {
          errorMessage = err.message;
        } else if (err instanceof Error) {
          errorMessage = err.message;
        }

        setError(errorMessage);
        onError?.(errorMessage);
        return null;
      } finally {
        setIsTranscribing(false);
      }
    },
    [onTranscribed, onLowConfidence, onError, language]
  );

  /**
   * Clear the current result and error.
   */
  const clear = useCallback(() => {
    setResult(null);
    setError(null);
    setIsLowConfidence(false);
  }, []);

  /**
   * Update service configuration.
   */
  const updateConfig = useCallback((newConfig: Partial<SpeechToTextConfig>) => {
    serviceRef.current.updateConfig(newConfig);
  }, []);

  return {
    isTranscribing,
    result,
    error,
    isLowConfidence,
    transcribe,
    clear,
    updateConfig,
  };
}

export default useSpeechToText;
